/**
 * @description Privacy page for the application. Displays legal information about GDPR and data processing,
 * and provides contact details.
 */

import { useEffect } from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Shield, Lock, Eye, Database, UserCheck, AlertTriangle, Link, Info, Mail } from "lucide-react";

interface PrivacyProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

export function Privacy({
  navigate,
  currentUser,
  onLogout,
  onNavigateToSearchPage,
}: PrivacyProps) {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const secondaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const warningPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 12%, var(--background))",
    borderLeftColor:
      "color-mix(in srgb, var(--accent) 70%, var(--background))",
  } as const;
  const warningIconStyle = {
    color: "color-mix(in srgb, var(--accent) 85%, var(--foreground))",
  } as const;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-8">
            <h2 className="text-primary-heading mb-2">{t("privacy.title")}</h2>
            <p className="text-secondary" style={secondaryTextStyle}>
              {t("privacy.subtitle")}
            </p>
          </div>

          <div className="space-y-6">

            <div
              className="border-l-4 p-4 rounded-r-md shadow-sm"
              style={warningPanelStyle}
            >
              <div className="flex items-start">
                <AlertTriangle
                  className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0"
                  style={warningIconStyle}
                />
                <div>
                  <h3 className="text-sm font-semibold" style={primaryTextStyle}>
                     {t("legalNotice.projectDisclaimerTitle")}
                  </h3>
                  <p className="text-sm mt-1" style={secondaryTextStyle}>
                    {t("legalNotice.projectDisclaimerText")}
                  </p>
                </div>
              </div>
            </div>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Info className="w-5 h-5" style={iconStyle} />
                  {t("privacy.introTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>{t("privacy.introText")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Database className="w-5 h-5" style={iconStyle} />
                  {t("privacy.section1Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                 <p>{t("privacy.section1Text")}</p>
              </CardContent>
            </Card>

             <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <UserCheck className="w-5 h-5" style={iconStyle} />
                  {t("privacy.section2Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                 <p>{t("privacy.section2Text")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Link className="w-5 h-5" style={iconStyle} />
                  {t("privacy.section3Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>{t("privacy.section3Text")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Lock className="w-5 h-5" style={iconStyle} />
                  {t("privacy.section4Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>{t("privacy.section4Text")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Shield className="w-5 h-5" style={iconStyle} />
                  {t("privacy.section5Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>{t("privacy.section5Text")}</p>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Mail className="w-5 h-5" style={iconStyle} />
                  {t("privacy.section6Title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-body" style={primaryTextStyle}>
                <p>
                  {t("privacy.section6Text")}
                  <br />
                  <br />
                  <strong>{t("privacy.contactName")}</strong>
                  <br />
                  {t("privacy.contactUni")}
                  <br />
                  Innstraße 33<br />
                  94032 Passau
                  <br />
                  <br />
                  E-Mail:{" "}
                  <a
                    href={`mailto:${t("privacy.contactMail")}`}
                    className="text-[var(--primary)] hover:underline"
                  >
                    {t("privacy.contactMail")}
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}

